<script setup>
import { useLayout } from '@/layout/common/composables/layout';
import { useDirection } from '@/composables/useDirection';
import { cva } from 'class-variance-authority';
import { computed } from 'vue';

const props = defineProps({
    class: {
        type: String,
        default: ''
    }
});

const SIDEBAR_WIDTH = {
    static: '16rem',
    slim: '5rem',
    compact: '4.5rem',
    horizontal: '100%',
    overlay: '18rem',
    drawer: '16rem',
    reveal: '16rem'
};

const { layoutConfig, calculatedMenuMode, onClose, setMenuState } = useLayout();
const { isRtl } = useDirection();

const handleBackdropClick = () => {
    if (calculatedMenuMode.value === 'overlay') {
        onClose();
    }
};

const handleMouseEnter = () => {
    if ((calculatedMenuMode.value === 'drawer' || calculatedMenuMode.value === 'reveal') && !layoutConfig.anchored) {
        setMenuState('expanded');
    }
};

const handleMouseLeave = () => {
    if ((calculatedMenuMode.value === 'drawer' || calculatedMenuMode.value === 'reveal') && !layoutConfig.anchored) {
        setMenuState('collapsed');
    }
};

const sidebarVariants = cva('group/sidebar-wrapper sidebar-full-menu !duration-200 ease-linear -my-2 flex ', {
    variants: {
        variant: {
            static: 'relative h-full fixed inset-y-0 flex-col w-[--sidebar-width] transition-[transform,max-width,left,right]',
            slim: 'relative h-full flex-col w-[--sidebar-width] ',
            overlay:
                'fixed h-full flex-col pt-4 top-0 z-[1000] dark:border-r w-full min-[400px]:w-[--sidebar-width] h-full shadow-lg data-[state=collapsed]:pointer-events-none data-[state=expanded]:pointer-events-auto overflow-hidden transition-[transform,max-width]',
            compact: 'relative h-full flex-col w-[--sidebar-width] ',
            horizontal: 'relative !my-0 w-full min-h-[4.75rem] max-h-[4.75rem] flex-row items-center',
            drawer: 'relative h-full flex-col data-[state=collapsed]:w-[4.5rem] data-[state=expanded]:w-[--sidebar-width] !transition-[width]',
            reveal: '!items-end relative h-full flex-col data-[state=collapsed]:w-[4.5rem] data-[state=expanded]:w-[--sidebar-width] !transition-[width]'
        },
        state: {
            expanded: '',
            collapsed: ''
        }
    },
    defaultVariants: {
        variant: 'static',
        state: 'expanded'
    }
});

const sidebarWrapperVariants = cva('relative z-[1000] transition-[transform,max-width,left] !duration-200 ease-linear ', {
    variants: {
        variant: {
            static: 'data-[state=collapsed]:w-0 data-[state=expanded]:w-[--sidebar-width] transition-[width] duration-200 ease-linear overflow-visible',
            horizontal: 'w-full shrink-0 mx-auto max-w-[1720px]',
            drawer: 'overflow-hidden'
        }
    },
    defaultVariants: {
        variant: 'static'
    }
});
const sidebarClass = computed(() => {
    const baseClass = sidebarVariants({
        variant: calculatedMenuMode.value,
        state: layoutConfig.menuState,
        class: props.class
    });
    
    // Add RTL-aware positioning for static mode
    let rtlClasses = '';
    if (calculatedMenuMode.value === 'static') {
        if (isRtl.value) {
            // RTL: position on right side, slide out to right when collapsed
            rtlClasses = 'right-0 left-auto data-[state=collapsed]:translate-x-full data-[state=expanded]:translate-x-0 data-[state=collapsed]:pointer-events-none';
        } else {
            // LTR: position on left side, slide out to left when collapsed  
            rtlClasses = 'left-0 right-auto data-[state=collapsed]:-translate-x-full data-[state=expanded]:translate-x-0 data-[state=collapsed]:pointer-events-none';
        }
    } else if (calculatedMenuMode.value === 'overlay') {
        if (isRtl.value) {
            // RTL: overlay from right
            rtlClasses = 'right-0 left-auto data-[state=collapsed]:translate-x-full data-[state=expanded]:translate-x-0';
        } else {
            // LTR: overlay from left  
            rtlClasses = 'left-0 right-auto data-[state=collapsed]:-translate-x-full data-[state=expanded]:translate-x-0';
        }
    }
    
    return `${baseClass} ${rtlClasses}`;
});

const sidebarWidth = computed(() => {
    return SIDEBAR_WIDTH[calculatedMenuMode.value];
});

const sidebarWrapperClass = computed(() => {
    const baseClass = sidebarWrapperVariants({
        variant: calculatedMenuMode.value,
        state: layoutConfig.menuState
    });
    
    // Add RTL-aware positioning for wrapper
    let rtlClasses = '';
    if (calculatedMenuMode.value === 'static') {
        if (isRtl.value) {
            // RTL: wrapper on right side
            rtlClasses = 'ml-auto mr-0 data-[state=collapsed]:w-0 data-[state=expanded]:w-[--sidebar-width] transition-[width] duration-200 ease-linear overflow-visible';
        } else {
            // LTR: wrapper on left side
            rtlClasses = 'mr-auto ml-0 data-[state=collapsed]:w-0 data-[state=expanded]:w-[--sidebar-width] transition-[width] duration-200 ease-linear overflow-visible';
        }
    }
    
    return `${baseClass} ${rtlClasses}`;
});
</script>

<template>
    <div
        v-if="calculatedMenuMode === 'overlay'"
        :data-state="layoutConfig.menuState"
        :data-variant="calculatedMenuMode"
        class="fixed inset-0 z-[999] bg-black/25 dark:bg-black/75 data-[state=collapsed]:pointer-events-none data-[state=collapsed]:opacity-0 data-[state=expanded]:pointer-events-auto data-[state=expanded]:opacity-100 transition-all duration-300"
        @click="handleBackdropClick"
    />
    <nav
        aria-label="Primary navigation"
        :class="sidebarWrapperClass"
        :style="{
            '--sidebar-width': sidebarWidth
        }"
        :data-state="layoutConfig.menuState"
        :data-variant="calculatedMenuMode"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
    >
        <div
            :class="sidebarClass"
            :style="{
                '--sidebar-width': sidebarWidth
            }"
            :data-state="layoutConfig.menuState"
            :data-variant="calculatedMenuMode"
            class="sidebar-bg"
        >
            <slot />
        </div>
    </nav>
</template>

<style lang="scss">
/* Sidebar dark mode - unscoped so .app-dark ancestor selector works */
.app-dark .sidebar-bg {
  background: var(--p-surface-900) !important;
  border-inline-end: 1px solid var(--p-surface-700) !important;
}
</style>

<style lang="scss" scoped>
/* Sidebar background - light mode */
.sidebar-bg {
  background: var(--sfh-surface-card, #ffffff);
  border-inline-end: 1px solid var(--sfh-border-subtle, rgba(0, 0, 0, 0.06));
}

/* RTL-specific sidebar positioning fixes */
html[dir="rtl"] .group\/sidebar-wrapper[data-variant="static"] {
  /* Position wrapper on right side */
  margin-left: auto !important;
  margin-right: 0 !important;
  
  /* Ensure proper RTL collapse/expand */
  &[data-state="collapsed"] {
    width: 0 !important;
    overflow: hidden;
  }
  
  &[data-state="expanded"] {
    width: var(--sidebar-width) !important;
  }
}

/* Ensure sidebar content is positioned correctly */
html[dir="rtl"] .sidebar-full-menu[data-variant="static"] {
  right: 0 !important;
  left: auto !important;
  transition: transform 200ms ease-linear !important;
  
  &[data-state="collapsed"] {
    transform: translateX(100%) !important;
    pointer-events: none;
  }
  
  &[data-state="expanded"] {
    transform: translateX(0) !important;
    pointer-events: auto;
  }
}

/* RTL overlay positioning */
html[dir="rtl"] .sidebar-full-menu[data-variant="overlay"] {
  right: 0 !important;
  left: auto !important;
  
  &[data-state="collapsed"] {
    transform: translateX(100%) !important;
  }
  
  &[data-state="expanded"] {
    transform: translateX(0) !important;
  }
}

/* Ensure backdrop works in RTL */
html[dir="rtl"] .fixed.inset-0.z-\[999\] {
  right: 0;
  left: 0;
}

/* Override any conflicting LTR styles */
html[dir="rtl"] .sidebar-full-menu {
  &[data-state="collapsed"] {
    left: auto !important;
    right: 0 !important;
  }
}
</style>
